import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * shadcn/ui-style Popover, reimplemented without @radix-ui/react-popover.
 *
 *   <Popover>
 *     <Popover.Trigger asChild>
 *       <Button>Open</Button>
 *     </Popover.Trigger>
 *     <Popover.Content side="bottom" align="start">
 *       <p>Popover body</p>
 *     </Popover.Content>
 *   </Popover>
 *
 * Handles: click-outside to close, Escape to close, viewport-aware
 * positioning (flips side if there isn't room), and portals content to
 * document.body so it isn't clipped by an ancestor's overflow/transform.
 *
 * What this does NOT reimplement from Radix: full focus trapping/return-focus
 * semantics, and pointer-vs-touch dismiss nuance. For a simple info/menu
 * popover this is usually enough; for anything with complex focusable
 * content (forms, multi-step content) revisit whether Radix is worth adding
 * back.
 */

const PopoverContext = createContext(null)

const usePopoverContext = () => {
  const ctx = useContext(PopoverContext)
  if (!ctx) {
    throw new Error('Popover.Trigger / Popover.Content must be used inside <Popover>')
  }
  return ctx
}

const Popover = ({ children, open: controlledOpen, defaultOpen = false, onOpenChange }) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen

  const setOpen = useCallback(
    (next) => {
      const resolveNext = (prev) => (typeof next === 'function' ? next(prev) : next)
      if (!isControlled) {
        setUncontrolledOpen(resolveNext)
      }
      onOpenChange?.(typeof next === 'function' ? next(open) : next)
    },
    [isControlled, onOpenChange, open]
  )

  const triggerRef = useRef(null)

  return (
    <PopoverContext.Provider value={{ open, setOpen, triggerRef }}>
      {children}
    </PopoverContext.Provider>
  )
}

const PopoverTrigger = ({ children, asChild = false, ...props }) => {
  const { open, setOpen, triggerRef } = usePopoverContext()

  const handleClick = (e) => {
    children.props?.onClick?.(e)
    props.onClick?.(e)
    setOpen(!open)
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ref: (node) => {
        triggerRef.current = node
        const { ref } = children
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      },
      onClick: handleClick,
      'aria-expanded': open,
      'aria-haspopup': 'dialog',
    })
  }

  return (
    <button
      type='button'
      ref={triggerRef}
      onClick={handleClick}
      aria-expanded={open}
      aria-haspopup='dialog'
      {...props}
    >
      {children}
    </button>
  )
}

const PopoverContent = ({
  children,
  className = '',
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}) => {
  const { open, setOpen, triggerRef } = usePopoverContext()
  const contentRef = useRef(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [resolvedSide, setResolvedSide] = useState(side)

  const computePosition = useCallback(() => {
    const trigger = triggerRef.current
    const content = contentRef.current
    if (!trigger || !content) return

    const triggerRect = trigger.getBoundingClientRect()
    const contentRect = content.getBoundingClientRect()
    const viewportW = window.innerWidth
    const viewportH = window.innerHeight

    let nextSide = side

    // Flip to the opposite side if there isn't room, matching Radix's
    // collision-avoidance behavior at a basic level.
    if (side === 'bottom' && triggerRect.bottom + contentRect.height + sideOffset > viewportH) {
      nextSide = 'top'
    } else if (side === 'top' && triggerRect.top - contentRect.height - sideOffset < 0) {
      nextSide = 'bottom'
    } else if (side === 'right' && triggerRect.right + contentRect.width + sideOffset > viewportW) {
      nextSide = 'left'
    } else if (side === 'left' && triggerRect.left - contentRect.width - sideOffset < 0) {
      nextSide = 'right'
    }

    let top = 0
    let left = 0

    switch (nextSide) {
      case 'top':
        top = triggerRect.top - contentRect.height - sideOffset
        break
      case 'bottom':
        top = triggerRect.bottom + sideOffset
        break
      case 'left':
        left = triggerRect.left - contentRect.width - sideOffset
        top = triggerRect.top
        break
      case 'right':
        left = triggerRect.right + sideOffset
        top = triggerRect.top
        break
      default:
        break
    }

    if (nextSide === 'top' || nextSide === 'bottom') {
      if (align === 'start') left = triggerRect.left
      else if (align === 'end') left = triggerRect.right - contentRect.width
      else left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2
    } else {
      if (align === 'start') top = triggerRect.top
      else if (align === 'end') top = triggerRect.bottom - contentRect.height
      else top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2
    }

    // Keep it on-screen horizontally as a final clamp.
    left = Math.max(8, Math.min(left, viewportW - contentRect.width - 8))

    setPosition({ top: top + window.scrollY, left: left + window.scrollX })
    setResolvedSide(nextSide)
  }, [side, align, sideOffset, triggerRef])

  useLayoutEffect(() => {
    if (!open) return
    computePosition()
  }, [open, computePosition])

  useEffect(() => {
    if (!open) return

    const handleReposition = () => computePosition()
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onEscapeKeyDown?.(e)
        if (!e.defaultPrevented) setOpen(false)
      }
    }

    const handlePointerDown = (e) => {
      const content = contentRef.current
      const trigger = triggerRef.current
      if (content?.contains(e.target) || trigger?.contains(e.target)) return
      onInteractOutside?.(e)
      if (!e.defaultPrevented) setOpen(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('mousedown', handlePointerDown, true)
    document.addEventListener('touchstart', handlePointerDown, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('mousedown', handlePointerDown, true)
      document.removeEventListener('touchstart', handlePointerDown, true)
    }
  }, [open, computePosition, setOpen, onEscapeKeyDown, onInteractOutside, triggerRef])

  if (!open) return null

  return createPortal(
    <div
      ref={contentRef}
      role='dialog'
      data-side={resolvedSide}
      className={['popover-content', className].filter(Boolean).join(' ')}
      style={{ top: position.top, left: position.left }}
      {...props}
    >
      {children}
    </div>,
    document.body
  )
}

Popover.Trigger = PopoverTrigger
Popover.Content = PopoverContent

export default Popover
export { PopoverTrigger, PopoverContent }
